import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MessageCirclePlus, ChevronRight, Users, Lock } from "lucide-react";

interface Row {
  id: string;
  question_text: string;
  status: "open" | "pending" | "answered" | "closed";
  route: "gathering" | "private";
  answer_text: string | null;
  answered_at: string | null;
  notified_at: string | null;
  created_at: string;
}

const STATUS_LABEL: Record<Row["status"], string> = {
  open: "Waiting for the congregation",
  pending: "Coming soon",
  answered: "Answered",
  closed: "Not taken up",
};



const STATUS_VARIANT: Record<Row["status"], "default" | "secondary" | "outline"> = {
  open: "secondary",
  pending: "secondary",
  answered: "default",
  closed: "outline",
};

export default function MyQuestions() {
  const { user } = useAuth();
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["my-gathering-questions-full", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("list_my_gathering_questions");
      if (error) throw error;
      return (data ?? []) as Row[];
    },
  });

  return (
    <div className="container max-w-3xl mx-auto px-4 py-8 space-y-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2 title-rule">
          <MessageCirclePlus className="w-6 h-6 text-gold" />
          Your questions
        </h1>

        <p className="text-sm text-muted-foreground mt-1">
          Everything you've brought to the weekly congregation, and the answers when they arrive.
        </p>
      </div>

      {isLoading ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">Loading...</CardContent></Card>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            You haven't brought a question yet. Open the dashboard and use "Bring a question."
          </CardContent>
        </Card>
      ) : (
        <ul className="space-y-3">
          {rows.map((q) => (
            <li key={q.id}>
              <Link
                to={`/support/questions/${q.id}`}
                className="block rounded-lg border bg-card hover:bg-muted/40 transition-colors"
              >
                <Card className="border-0 shadow-none bg-transparent">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <CardTitle className="text-base font-medium flex-1 min-w-0 line-clamp-2 whitespace-normal">
                        {q.question_text}
                      </CardTitle>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Badge variant="outline" className="text-[10px]">
                          {q.route === "private" ? (
                            <><Lock className="w-3 h-3 mr-1" />Private</>
                          ) : (
                            <><Users className="w-3 h-3 mr-1" />For the congregation</>
                          )}
                        </Badge>
                        <Badge variant={STATUS_VARIANT[q.status]}>
                          {STATUS_LABEL[q.status]}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Asked {formatDistanceToNow(new Date(q.created_at), { addSuffix: true })}
                      {q.answered_at && ` · Answered ${formatDistanceToNow(new Date(q.answered_at), { addSuffix: true })}`}
                    </p>
                  </CardHeader>
                  {q.status === "answered" && q.answer_text && (
                    <CardContent className="pt-0">
                      <div className="rounded-md bg-muted/60 p-3 text-sm whitespace-pre-wrap line-clamp-4">
                        {q.answer_text}
                      </div>
                      <div className="flex items-center justify-end text-xs text-muted-foreground mt-2">
                        Read the full answer <ChevronRight className="w-3 h-3 ml-1" />
                      </div>
                    </CardContent>
                  )}
                </Card>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
